import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { MapPin, Zap, Network, TrendingUp } from 'lucide-react';

const Project = () => {
  const projectHighlights = [
    {
      icon: Network,
      title: 'Grid-connected solar power generation',
      description: 'Seamlessly integrated with state power infrastructure'
    },
    {
      icon: TrendingUp,
      title: 'High efficiency & long-term reliability',
      description: 'Designed for decades of optimal performance'
    },
    {
      icon: Zap,
      title: 'Government & DISCOM standards',
      description: 'Full compliance with regulatory requirements'
    },
    {
      icon: MapPin,
      title: 'Significant carbon reduction',
      description: 'Contributing to India\'s climate goals'
    }
  ];

  return (
    <section id="project" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-block px-4 py-2 bg-emerald-50 rounded-full text-emerald-700 font-medium text-sm mb-4">
            Flagship Project
          </div>
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            TenSolar – 10 MW Solar Power Plant
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            A utility-scale solar installation powering communities across Madhya Pradesh
          </p>
        </div>

        {/* Project Details */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start mb-16">
          {/* Left - Map Visual */}
          <div className="relative">
            <Card className="overflow-hidden border-gray-200 shadow-lg">
              <CardContent className="p-0">
                <div className="relative bg-gradient-to-br from-emerald-50 to-amber-50 p-12">
                  {/* Simple India Map Representation */}
                  <div className="relative mx-auto" style={{ maxWidth: '300px' }}>
                    <svg viewBox="0 0 300 400" className="w-full h-auto">
                      {/* Simplified India outline */}
                      <path
                        d="M150 20 L180 50 L200 80 L210 120 L220 160 L225 200 L220 240 L210 280 L200 320 L190 360 L170 390 L150 395 L130 390 L110 360 L100 320 L90 280 L80 240 L75 200 L80 160 L90 120 L100 80 L120 50 Z"
                        fill="#f0fdf4"
                        stroke="#059669"
                        strokeWidth="2"
                      />
                      {/* Madhya Pradesh highlight (central region) */}
                      <circle
                        cx="150"
                        cy="180"
                        r="30"
                        fill="#059669"
                        opacity="0.3"
                        className="animate-pulse"
                      />
                      <circle
                        cx="150"
                        cy="180"
                        r="8"
                        fill="#059669"
                      />
                      {/* Label */}
                      <text
                        x="150"
                        y="230"
                        textAnchor="middle"
                        className="text-sm font-semibold fill-emerald-800"
                      >
                        Madhya Pradesh
                      </text>
                    </svg>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Project Stats Card */}
            <Card className="mt-6 border-emerald-200 bg-emerald-50">
              <CardHeader>
                <CardTitle className="text-emerald-900">Project Overview</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-700 font-medium">Capacity:</span>
                    <span className="text-emerald-800 font-bold text-lg">10 MW</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-700 font-medium">Location:</span>
                    <span className="text-emerald-800 font-bold">Madhya Pradesh</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-700 font-medium">Type:</span>
                    <span className="text-emerald-800 font-bold">Grid-connected</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right - Highlights */}
          <div className="space-y-6">
            <h3 className="text-2xl font-bold text-gray-900 mb-8">Project Highlights</h3>
            {projectHighlights.map((highlight, index) => (
              <div 
                key={index}
                className="flex gap-4 p-6 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors duration-300"
              >
                <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center flex-shrink-0 shadow-sm">
                  <highlight.icon className="w-6 h-6 text-emerald-700" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">{highlight.title}</h4>
                  <p className="text-gray-600 text-sm leading-relaxed">{highlight.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Project;
